// src/pages/groups/RichText.jsx
// بيعرض HTML اللي جاي من الـ text editor (bold / italic / قوائم / ألوان ...) بشكل آمن.
// لو النص عادي (من غير تاجات) بيتعرض كنص عادي.
import { useMemo } from "react";

const ALLOWED = new Set([
  "P", "BR", "STRONG", "B", "EM", "I", "U", "S", "STRIKE", "SUB", "SUP", "MARK",
  "UL", "OL", "LI", "SPAN", "DIV", "H1", "H2", "H3", "H4", "BLOCKQUOTE", "CODE", "PRE", "A",
]);
const DROP = new Set([
  "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META", "FORM", "INPUT", "BUTTON", "SVG", "MATH", "IMG",
]);
const SAFE_STYLE_PROP = /^(color|background-color|text-align|font-weight|font-style|text-decoration|direction)$/;
const SAFE_STYLE_VALUE = /^[#\w\s(),.%-]+$/;
const UNSAFE_VALUE = /url|expression|javascript/i;

function clean(node) {
  [...node.childNodes].forEach((child) => {
    if (child.nodeType === 3) return; // text
    if (child.nodeType !== 1) {
      child.remove();
      return;
    }
    const tag = child.tagName;
    if (DROP.has(tag)) {
      child.remove();
      return;
    }
    clean(child);
    if (!ALLOWED.has(tag)) {
      child.replaceWith(...child.childNodes); // نشيل التاج ونسيب محتواه
      return;
    }
    [...child.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name === "style") {
        const kept = attr.value
          .split(";")
          .map((s) => s.trim())
          .filter(Boolean)
          .filter((decl) => {
            const [prop, ...rest] = decl.split(":");
            const value = rest.join(":").trim();
            return (
              SAFE_STYLE_PROP.test(prop.trim().toLowerCase()) &&
              SAFE_STYLE_VALUE.test(value) &&
              !UNSAFE_VALUE.test(value)
            );
          })
          .join(";");
        if (kept) child.setAttribute("style", kept);
        else child.removeAttribute("style");
      } else if (name === "href" && tag === "A" && /^(https?:|mailto:)/i.test(attr.value.trim())) {
        // آمن
      } else if (name === "dir") {
        // آمن
      } else {
        child.removeAttribute(attr.name);
      }
    });
    if (tag === "A") {
      child.setAttribute("target", "_blank");
      child.setAttribute("rel", "noopener noreferrer");
    }
  });
}

export function sanitizeHtml(html) {
  if (typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  clean(doc.body);
  return doc.body.innerHTML;
}

const looksLikeHtml = (s) => /<[a-z][\s\S]*>/i.test(s);

// ستايل بسيط عشان الفقرات ما تعملش مسافات كبيرة جوه القوايم
const CSS = `
.gp-rich p{margin:0 0 .35em}
.gp-rich p:last-child{margin-bottom:0}
.gp-rich ul,.gp-rich ol{margin:.25em 0;padding-inline-start:1.4em}
.gp-rich blockquote{margin:.3em 0;padding-inline-start:.8em;border-inline-start:3px solid #ddd}
.gp-rich pre,.gp-rich code{font-family:monospace;background:#f3f4f6;border-radius:4px}
.gp-rich pre{padding:6px 8px;overflow-x:auto}
`;
if (typeof document !== "undefined" && !document.getElementById("gp-rich-css")) {
  const el = document.createElement("style");
  el.id = "gp-rich-css";
  el.textContent = CSS;
  document.head.appendChild(el);
}

/**
 * html   : النص (HTML أو نص عادي)
 * clamp  : عدد الأسطر القصوى (اختياري) — للعرض المختصر في القوايم
 */
export default function RichText({ html, clamp, style }) {
  const value = html || "";
  const safe = useMemo(() => (looksLikeHtml(value) ? sanitizeHtml(value) : null), [value]);

  const clampStyle = clamp
    ? { display: "-webkit-box", WebkitLineClamp: clamp, WebkitBoxOrient: "vertical", overflow: "hidden" }
    : {};

  if (safe === null) {
    return (
      <div dir="auto" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", ...clampStyle, ...style }}>
        {value}
      </div>
    );
  }
  return (
    <div
      dir="auto"
      className="gp-rich"
      style={{ wordBreak: "break-word", ...clampStyle, ...style }}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}